// Combination of
// https://stackoverflow.com/a/50736992/18059803
// and
// https://stackoverflow.com/a/43589069/18059803

// This directive does two things:
// Converts a decimal to a percentage for a display (but keeps the decimal for saving)
// and
// Allows the consuming component to specify the number of decimal places to display

// Usage
// <input type="text" toPercentage [precision]="3" [(ngModel)]="percentageInDecimalForm">

import { Directive, ElementRef, Input, forwardRef, HostListener, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
    selector: '[toPercentage]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ToPercentDirective),
            multi: true,
        },
    ],
    host: {
        '(blur)': 'onBlur()',
    },
})
export class ToPercentDirective implements ControlValueAccessor {
    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
    ) {
        this.el.nativeElement.style.textAlign = 'right';
    }

    @Input() precision: number;
    @Input() allowText: boolean;

    private innerValue: string;
    private toNumber: number;
    private toPercent: number;
    private regex: RegExp = new RegExp(/^\d*\.?\d{0,3}$/g);
    private specialKeys: Array<string> = [
        'Backspace',
        'Tab',
        'End',
        'Home',
        '-',
        'ArrowLeft',
        'ArrowRight',
        'Del',
        'Delete',
    ];

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        // If the input is disabled, do nothing and let Angular/native behavior handle it
        if (this.el.nativeElement.disabled) {
            return;
        }
        // Allow Backspace, tab, end, and home keys
        if (!this.precision || this.specialKeys.indexOf(event.key) !== -1) {
            return;
        }

        const current: string = this.el.nativeElement.value;

        if (this.allowText && isNaN(Number(current))) {
            return;
        }

        const position = this.el.nativeElement.selectionStart;
        const next: string = [
            current.slice(0, position),
            event.key == 'Decimal' ? '.' : event.key,
            current.slice(position),
        ].join('');
        if (next && !String(next).match(new RegExp(`^\\d*\\.?\\d{0,${this.precision}}$`, 'g'))) {
            event.preventDefault();
        }
    }

    public onChangeCallback: (value: unknown) => void = () => undefined;
    public onTouched: () => void = () => {
        /*Empty*/
    };

    onBlur() {
        if (this.el.nativeElement.disabled) {
            return;
        }

        let input = this.el.nativeElement.value;
        if (!input) {
            input = 0;
        }

        if (this.allowText && isNaN(Number(input))) {
            const numberValue = input.replace(/[^0-9]/g, '');
            if (!numberValue) {
                this.el.nativeElement.value = input;
                return;
            } else {
                input = input.replace(/[^0-9.]/g, '').replace(/^(\d*\.?\d{0,1}).*$/, '$1');
            }
        }

        this.toPercent = new Decimal(input).dividedBy(100).toNumber();
        if (new Decimal(input).toNumber() !== this.toNumber) {
            this.toNumber = new Decimal(input).toNumber();
            this.onChangeCallback(this.toPercent);
        }
        this.el.nativeElement.value = this.toNumber.toFixed(this.precision);
    }

    get value(): string {
        return this.innerValue;
    }

    // set accessor including call the onchange callback
    set value(v: string) {
        if (v !== this.innerValue) {
            this.innerValue = v;
            this.onChangeCallback(v);
        }
    }

    writeValue(val: string): void {
        if (this.allowText && isNaN(Number(val))) {
            this.toNumber = null;
            this.el.nativeElement.value = String(val);
        } else {
            this.toNumber = new Decimal(val || 0).times(100).toNumber();
            this.el.nativeElement.value = this.toNumber.toFixed(this.precision);
        }
    }

    registerOnChange(fn: (value: unknown) => void): void {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    // Called by Angular when the FormControl is enabled/disabled
    setDisabledState(isDisabled: boolean): void {
        this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
    }
}
